/**
 * 运营总览 · 深度分析 + 预测 — /ops/analytics/deep
 * 对齐 canvas「运营总览 - 深度分析」(Zco-FHD5FvbMQI0)·月度真聚合·NPS/客户健康度=代表值(无评分体系)。
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { analyticsApi } from "@/services/analytics";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { OperationsTabs } from "./OperationsTabs";

const usd = (c: number) => `$${(c / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const CH: Record<string, string> = { b2b: "🤝 B2B", api: "🔌 API", social: "📞 社媒", client: "💻 直客" };
const HEALTH_COLOR: Record<string, string> = { 优秀: "bg-emerald-500", 良好: "bg-blue-500", 一般: "bg-amber-500", 风险: "bg-rose-500" };

export function DeepAnalysisPage() {
  const { data: d, isLoading, isError, refetch } = useQuery({ queryKey: ["analytics", "deep"], queryFn: analyticsApi.deep });
  if (isLoading) return <LoadingState />;
  if (isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>深度分析加载失败</span>
      <button onClick={() => refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!d) return <LoadingState />;

  const masked = d.pl_summary._masked;
  const maxTrend = Math.max(1, ...d.monthly_trend.map((t) => t.revenue_cents));

  return (
    <PageShell title="📊 运营总览 · 深度分析 + 预测" subtitle="月度/季度 · MVP 简单外推预测（v2 上 ML）· 老板战略看">
      <OperationsTabs />
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
        <Lock size={14} /> 经理可见全部业务数据 · 唯独「月度净利」不可见（🔒 财务专属）
      </div>

      {/* 6 KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="月度 GMV" value={usd(d.gmv_month_cents)} sub={`↗ ${d.gmv_mom_pct}% MoM · ${d.gmv_yoy_pct}% YoY`} accent />
        <Kpi label="月度净利" value={masked ? "🔒" : usd(d.pl_summary.net_profit_cents ?? 0)} sub={masked ? "财务专属" : "净利率见财务"} tint="lock" />
        <Kpi label="月度订单" value={String(d.orders_month)} sub={`↗ +${d.orders_mom_delta} vs 上月`} />
        <Kpi label="月度新客" value={String(d.new_customers_month)} sub="本月首单客户" />
        <Kpi label="客户 NPS" value={String(d.nps)} sub="内部评分 · 行业 50+" />
        <Kpi label="复购率" value={`${d.repeat_rate_pct}%`} sub="老客户重复下单" />
      </div>

      {/* 月度 GMV 走势 */}
      <Card title="📈 月度 GMV 走势（近 12 月）">
        <div className="flex h-32 items-end gap-1.5">
          {d.monthly_trend.map((t) => (
            <div key={t.month} className="flex flex-1 flex-col items-center gap-1">
              <div className="text-[9px] text-slate-500">{usd(t.revenue_cents)}</div>
              <div className="w-full rounded-t bg-blue-500" style={{ height: `${(t.revenue_cents / maxTrend) * 90}px` }} />
              <div className="text-[9px] text-slate-400">{t.month.slice(5)}月</div>
            </div>
          ))}
        </div>
        <div className="mt-1 text-[11px] text-slate-500">近 12 月累计 {usd(d.monthly_trend.reduce((a, t) => a + t.revenue_cents, 0))}</div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 渠道走势 */}
        <Card title="🎯 渠道贡献占比走势（本月 vs 上月）">
          <div className="flex flex-col gap-2">
            {d.channel_share.map((c) => (
              <div key={c.channel}>
                <div className="flex items-center justify-between text-[12px]"><span className="text-slate-600">{CH[c.channel] ?? c.channel}</span><span className="font-semibold text-slate-800">{c.pct}% <span className="text-[10px] text-slate-400">(上月 {c.prev_pct}%)</span></span></div>
                <div className="mt-0.5 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-blue-500" style={{ width: `${c.pct}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>

        {/* B2B vs 直客 YoY */}
        <Card title="📊 B2B vs 直客 增长对比（YoY）">
          <div className="flex flex-col gap-3">
            {d.yoy_growth.map((g) => (
              <div key={g.channel} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-[13px] font-medium text-slate-700">{CH[g.channel] ?? g.channel}</span>
                <span className="text-[13px]">GMV <b className={g.gmv_yoy_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{g.gmv_yoy_pct >= 0 ? "+" : ""}{g.gmv_yoy_pct}%</b> · 订单 <b className="text-slate-700">{g.orders_yoy_pct >= 0 ? "+" : ""}{g.orders_yoy_pct}%</b></span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* 产品 Top */}
      <Card title="🏆 产品 Top 10 排行 + 毛利（本月按净利贡献）">
        <div className="flex flex-col gap-1.5">
          {d.top_products.length === 0 && <div className="py-4 text-center text-[12px] text-slate-400">本月暂无产品订单</div>}
          {d.top_products.map((p, i) => (
            <div key={p.product + i} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[12px] font-bold text-blue-700">{i + 1}</span>
              <div className="min-w-0 flex-1"><div className="truncate text-[13px] font-semibold text-slate-800">{p.product}</div><div className="text-[11px] text-slate-500">{p.orders} 单 · {p.margin_pct}% 毛利</div></div>
              <div className="text-[14px] font-bold text-emerald-700">{usd(p.net_cents)}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 库存利用率 */}
        <Card title="📦 库存利用率（3 资源月度）">
          <div className="flex flex-col gap-3">
            {d.inventory_util.map((r) => (
              <div key={r.resource}>
                <div className="flex items-center justify-between text-[12px]"><span className="text-slate-600">{r.resource}</span><span className="font-semibold text-slate-800">{r.util_pct}%</span></div>
                <div className="mt-0.5 h-2.5 rounded-full bg-slate-100"><div className={`h-2.5 rounded-full ${r.util_pct >= 85 ? "bg-rose-500" : r.util_pct >= 70 ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${r.util_pct}%` }} /></div>
              </div>
            ))}
            <div className="text-[11px] text-slate-500">💡 理想区间 70-85%</div>
          </div>
        </Card>

        {/* 客户健康度 */}
        <Card title="👥 客户健康度分布">
          <div className="flex flex-col gap-2">
            {d.customer_health.map((h) => (
              <div key={h.tier}>
                <div className="flex items-center justify-between text-[12px]"><span className="text-slate-600">{h.tier}</span><span className="font-semibold text-slate-800">{h.pct}%</span></div>
                <div className="mt-0.5 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${HEALTH_COLOR[h.tier] ?? "bg-slate-400"}`} style={{ width: `${h.pct}%` }} /></div>
              </div>
            ))}
            <div className="text-[11px] text-slate-400">代表值 · 客户评分体系待建（活跃度+付款+投诉）</div>
          </div>
        </Card>
      </div>

      {/* 预测 + 预警 */}
      <Card title="🔮 预测 + 预警（MVP 简单外推 · v2 上 ML）">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
            <div className="text-[12px] text-slate-500">🔮 下月 GMV 预测</div>
            <div className="mt-1 text-[24px] font-bold text-violet-700">{usd(d.forecast.next_gmv_cents)} <span className="text-[13px] font-normal text-slate-500">± {usd(d.forecast.band_cents)}</span></div>
            <div className="mt-1 text-[12px] text-slate-600">预估 {d.forecast.mom_pct >= 0 ? "+" : ""}{d.forecast.mom_pct}% MoM</div>
            <div className="mt-1 text-[11px] text-slate-500">依据：{d.forecast.basis}</div>
            <div className="mt-1 text-[12px] text-amber-700">{d.forecast.seasonality}</div>
            <div className="mt-1 text-[11px] text-slate-400">💡 简单线性外推 · v2 上 ML（多因素）</div>
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 flex items-center justify-between"><span className="text-[12px] font-semibold text-slate-700">🚨 4 类自动告警</span></div>
            <ul className="flex flex-col gap-1.5 text-[12px] text-slate-600">
              {d.alerts_by_type.map((a) => (
                <li key={a.type} className="flex items-center justify-between">
                  <span>{a.type}：<b className={a.count > 0 ? "text-rose-600" : "text-slate-400"}>{a.count}</b> <span className="text-[10px] text-slate-400">（{a.hint}）</span></span>
                  <Link to={a.link} className="text-[11px] text-blue-600">跳源 →</Link>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[10px] text-slate-400">⚠️ 告警生成引擎待建（系统告警工单）· 现读 InventoryAlert 表</div>
          </div>
        </div>
      </Card>

      {/* 🌞 季节性提示（独立 section·全年季节性日历）*/}
      <Card title="🌞 季节性提示（黄石全年）">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {d.forecast.season_calendar.map((s) => (
            <div key={s.period} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[13px] font-semibold text-slate-800">{s.label}</div>
              <div className="text-[11px] text-slate-500">{s.period}</div>
              <div className="mt-1 text-[12px] font-medium text-amber-700">{s.impact}</div>
            </div>
          ))}
        </div>
      </Card>
    </PageShell>
  );
}

function Kpi({ label, value, sub, accent, tint }: { label: string; value: string; sub: string; accent?: boolean; tint?: "lock" }) {
  const ring = tint === "lock" ? "border-slate-200 bg-slate-50" : accent ? "border-blue-200 bg-blue-50/60" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-4 ${ring}`}>
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className="mt-1 text-[20px] font-bold text-slate-900">{value}</div>
      <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 text-[14px] font-semibold text-slate-900">{title}</div>
      {children}
    </section>
  );
}
